// The BcfRouterService class is a service that provides navigation functionality.
// It can be used to navigate to a route, and to prepare a url.
// It also provides the base href.
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { pathJoin } from '@bcf-vanilla-ts-v1-shared/misc/pure-utils/path-join';
import type { BcfRouterBaseHref } from './bcf-router-base-href';
import { provideBcfRouterBaseHref } from './bcf-router-base-href';
import {
  provideBcfRouterStateEnhancerRegistry,
  type BcfRouterStateEnhancerRegistry
} from './bcf-router-state-enhancer';
import type { HistoryState } from './history-state';
import { provideHistoryState } from './history-state';
import { provideHistoryUrl, type HistoryUrl } from './history-url';
import type { NavigationExtras, Params } from './types';

function stringifyQueryParams(currentQueryParams: URLSearchParams, newQueryParams: Params): string {
  for (const [key, value] of Object.entries(newQueryParams)) {
    if (value === undefined) {
      currentQueryParams.delete(key);
    } else {
      currentQueryParams.set(key, value!.toString());
    }
  }
  return currentQueryParams.toString();
}

function addBaseHrefToUrl(url: string | undefined, baseHrefPath: string): string | undefined {
  if (!url) {
    return url;
  }
  if (url.startsWith('/casino/') || url.startsWith('/sports/')) {
    return url;
  }
  // when url already has /casino or /sports we dont want to add it again
  if (baseHrefPath.length > 1 && url.startsWith(baseHrefPath)) {
    return url;
  }
  return pathJoin(baseHrefPath, url);
}

function routerPrepareUrl(path: string, baseHrefPath: string, extras?: NavigationExtras): string {
  const queryParamsString: string | undefined = extras?.queryParams
    ? stringifyQueryParams(new URLSearchParams(''), extras.queryParams)
    : undefined;

  return (
    addBaseHrefToUrl(path, baseHrefPath) +
    (queryParamsString ? `?${queryParamsString}` : '') +
    (extras?.fragment ? `#${extras.fragment}` : '')
  );
}

export interface BcfRouterContract {
  navigate(path: string, extras?: NavigationExtras): void;

  goBack(): void;

  updateFragment(fragment: string | undefined, params?: string[]): void;

  updateQueryParams(queryParams: Params, strategy?: 'merge'): void;

  // The prepareUrl method prepares a url with the base href and the query params.
  prepareUrl(path: string, extras?: NavigationExtras): string;
}

export class BcfRouterService implements BcfRouterContract {
  constructor(
    private _historyState: HistoryState,
    private _historyUrl: HistoryUrl,
    private _bcfRouterBaseHref: BcfRouterBaseHref,
    private _enhancerRegistry: BcfRouterStateEnhancerRegistry
  ) {}

  // The navigate method navigates to a route.
  public navigate(path: string, extras?: NavigationExtras): void {
    let newState: Record<string, any> = {
      path: addBaseHrefToUrl(path, this._bcfRouterBaseHref.baseHref)
    };
    if (extras?.state) {
      newState = {
        ...newState,
        ...extras.state
      };
    }
    newState = this._enhancerRegistry.enhance(window.location.pathname, window.history.state, newState, extras);
    const preparedUrl: string = this.prepareUrl(path, extras);
    if (extras?.replaceUrl) {
      window.history.replaceState(newState, '', preparedUrl);
      this._historyState.stateMightChanged();
      this._historyUrl.routerMightReplaced();
      return;
    }
    window.history.pushState(newState, '', preparedUrl);
    this._historyState.stateMightChanged();
    this._historyUrl.routerMightUpdated();
  }

  public goBack(): void {
    this._historyState.back();
  }

  public updateFragment(fragment: string | undefined, params?: string[]): void {
    if (fragment && params && params.length > 0) {
      this.navigate(window.location.pathname, {
        fragment: `${fragment}$${params.join('~')}`
      });
      return;
    }
    this.navigate(window.location.pathname, {
      fragment: fragment
    });
  }

  public updateQueryParams(queryParams: Params, strategy?: 'merge'): void {
    this.navigate(window.location.pathname, {
      queryParams: queryParams,
      queryParamsHandling: strategy
    });
  }

  // The prepareUrl method prepares a url with the base href and the query params.
  public prepareUrl(path: string, extras?: NavigationExtras): string {
    return routerPrepareUrl(path, this._bcfRouterBaseHref.baseHref, extras);
  }
}

export function provideBcfRouterService(): BcfRouterService {
  return provideSingleton(
    BcfRouterService,
    () =>
      new BcfRouterService(
        provideHistoryState(),
        provideHistoryUrl(),
        provideBcfRouterBaseHref(),
        provideBcfRouterStateEnhancerRegistry()
      )
  );
}
